import { OrderByDirection } from 'common/interfaces/pagination';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { IHierarchyAPIQuery, TArchivesBody } from 'common/interfaces/hierarchy-entities';

export const HIERARCHY_ITEMS_LOAD_AMOUNT = 1000;

const URL_PREFIX = '/archive';

export type GetHierarchyEntitiesFunction = (params: Partial<IHierarchyAPIQuery>) => unknown;

export enum HierarchyEntityName {
  ARCHIVE = 'archive',
  COUNTRY = 'country',
  FIELD = 'field',
  SITE = 'site',
  WELL = 'well',
  WELLBORE = 'wellbore',
  DESIGN = 'design',
  NODE = 'node',
}

export type NodeCreateParentPayload = {
  parent: {
    type: HierarchyEntityName.COUNTRY | HierarchyEntityName.FIELD | HierarchyEntityName.SITE | HierarchyEntityName.WELL | HierarchyEntityName.WELLBORE;
    id: string;
  };
};

const getDefaultOrderBy = (entity: HierarchyEntityName) => (entity === 'design' ? 'well_status' : 'name');
export const compileGetHierarchyEntityUrl = (entity: HierarchyEntityName, [params]: Parameters<GetHierarchyEntitiesFunction>, [defaultParams]: Parameters<GetHierarchyEntitiesFunction> = [{
  limit: HIERARCHY_ITEMS_LOAD_AMOUNT, page: 1, orderByDirection: OrderByDirection.Asc, orderBy: getDefaultOrderBy(entity),
}]): string => {
  const defaultedParams = {
    ...defaultParams,
    ...params,
  };
  const getValue = (key: keyof typeof params) => defaultedParams[key];
  const paramsQuery = [
    `type=${entity}`,
    ...Object.keys(defaultedParams)
      .filter((paramName) => Boolean(getValue(paramName as any)))
      .map((paramName) => `${paramName}=${getValue(paramName as any)!.toString()}`),
  ].join('&');
  return `${URL_PREFIX}?${paramsQuery}`;
};

export const getHierarchyEntitiesAPICallAction = <T = any>(entityType: HierarchyEntityName, params: Partial<IHierarchyAPIQuery>, startType: string, errorType: string, responseType: string) => apiCallBegan({
  url: compileGetHierarchyEntityUrl(entityType, [params]),
  method: 'GET',
  onStart: () => ({ type: startType, payload: { parentId: params.parentId } }),
  onSuccess: (response: T[]) => ({
    type: responseType,
    payload: [response, params],
  }),
  onError: (e: any) => ({ type: errorType, payload: { parentId: params.parentId, message: e.message } }),
});

export const getHierarchyEntityAPICallAction = <T = any>(entityType: HierarchyEntityName, params: { entityId: string; parentId: string; }, startType: string, errorType: string, responseType: string) => apiCallBegan({
  url: `${URL_PREFIX}/${params.entityId}?type=${entityType}`,
  method: 'GET',
  onStart: () => ({ type: startType, payload: { parentId: params.parentId } }),
  onSuccess: (response: T) => ({
    type: responseType,
    payload: response,
  }),
  onError: (e: any) => ({ type: errorType, payload: { parentId: params.parentId, message: e.message } }),
});

export const postHierarchyEntityAPICallAction = (entityType: HierarchyEntityName, data: any, startType: string, errorType: string, responseType: string) => apiCallBegan({
  url: `/archive?type=${entityType}`,
  method: 'POST',
  data,
  toastOnError: true,
  onStart: () => ({ type: startType, payload: { parentId: '' } }),
  onSuccess: (response: TArchivesBody) => ({
    type: responseType,
    payload: response,
  }),
  onError: (e: any) => ({ type: errorType, payload: { parentId: '', message: e.message } }),
});

export const putHierarchyEntityAPICallAction = (entityType: HierarchyEntityName, entityId: string, data: any, startType: string, errorType: string, responseType: string) => apiCallBegan({
  url: `/archive/${entityId}?type=${entityType}`,
  method: 'PUT',
  data,
  toastOnError: true,
  onStart: () => ({ type: startType, payload: { parentId: '' } }),
  onSuccess: (response: { data: { id: string; }; }) => getHierarchyEntityAPICallAction(entityType, { entityId: response?.data?.id, parentId: '' }, startType, errorType, responseType),
  onError: (e: any) => ({ type: errorType, payload: { parentId: '', message: e.message } }),
});

export const deleteHierarchyEntityAPICallAction = (entityType: HierarchyEntityName, entityId: string, startType: string, errorType: string, sucessType: string) => apiCallBegan({
  url: `/archive/${entityId}?type=${entityType}`,
  method: 'delete',
  onStart: () => ({ type: startType, payload: { parentId: '' } }),
  onSuccess: () => ({
    type: sucessType,
    payload: entityId,
  }),
  onError: (e: any) => ({ type: errorType, payload: { parentId: '', message: e.message } }),
});
